import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStudioConfig } from "@/hooks/use-studio-config";
import { useLocation } from "wouter";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PWAInstallPromptProps {
  onDone?: () => void;
}

export function PWAInstallPrompt({ onDone }: PWAInstallPromptProps = {}) {
  const { config: studio } = useStudioConfig();
  const [location] = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [devPreview, setDevPreview] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const rafRef = useRef(0);

  const isClientPortal = location.startsWith("/my");

  useEffect(() => {
    if (isClientPortal) return;
    const handleScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setScrolledPastHero(window.scrollY > 400);
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isClientPortal]);

  useEffect(() => {
    if (localStorage.getItem("pwa-install-dismissed")) {
      setDismissed(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      setDevPreview(true);
      setShowPrompt(true);
      setDismissed(false);
    };
    window.addEventListener("dev-show-pwa-prompt", handler);
    return () => window.removeEventListener("dev-show-pwa-prompt", handler);
  }, []);

  useEffect(() => {
    const onEmailOpen = () => setEmailOpen(true);
    const onEmailClose = () => setEmailOpen(false);
    window.addEventListener("open-email-signup", onEmailOpen);
    window.addEventListener("email-signup-closed", onEmailClose);
    return () => {
      window.removeEventListener("open-email-signup", onEmailOpen);
      window.removeEventListener("email-signup-closed", onEmailClose);
    };
  }, []);

  const handleInstall = async () => {
    if (devPreview) {
      setShowPrompt(false);
      setDevPreview(false);
      window.dispatchEvent(new Event("pwa-prompt-dismissed"));
      onDone?.();
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
    window.dispatchEvent(new Event("pwa-prompt-dismissed"));
    onDone?.();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDevPreview(false);
    setDismissed(true);
    localStorage.setItem("pwa-install-dismissed", "1");
    window.dispatchEvent(new Event("pwa-prompt-dismissed"));
    onDone?.();
  };

  const visible = showPrompt && !dismissed && !emailOpen && (isClientPortal || scrolledPastHero);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-white/10 pwa-footer-hide"
          style={{ backgroundColor: "rgba(15,12,20,0.97)", backdropFilter: "blur(12px)" }}
          data-testid="pwa-install-prompt"
        >
          <div className="container mx-auto px-6 py-4 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Dismiss install prompt"
              data-testid="button-dismiss-pwa"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
            <div className="flex items-center gap-4 pr-10">
              <div className="shrink-0 w-12 h-12 rounded-xl overflow-hidden">
                <img src="/icon-192.png" alt={studio.shortName} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-subheading font-bold text-sm tracking-wide mb-0.5">
                  Install {studio.shortName} App
                </h4>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Add to your home screen for quick access to bookings, healing tracker, and more.
                </p>
              </div>
              <button
                onClick={handleInstall}
                className="shrink-0 bg-accent text-white px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-accent/80 transition-all duration-300"
                data-testid="button-install-pwa"
              >
                Install App
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
